import { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Tag,
  Form,
  Select,
  message,
  Popconfirm,
  Typography,
  Collapse,
  Descriptions,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { trpc } from '../../../lib/api';
import { PageHeader, PageContainer, withDevModalFilePath } from '../../../components/common';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Modal } = withDevModalFilePath('client/src/pages/admin/auth/AdminUserManagementPage.tsx');

interface AdminUser {
  id: string;
  username: string;
  email: string;
  displayName: string;
  department: string | null;
  mfaEnabled: boolean;
  lastLoginAt: Date | null;
  loginCount: number;
  isActive: boolean;
  roles: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeAdminUser = (value: unknown): AdminUser | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    username: typeof value.username === 'string' ? value.username : '',
    email: typeof value.email === 'string' ? value.email : '',
    displayName: typeof value.displayName === 'string' ? value.displayName : '',
    department: typeof value.department === 'string' ? value.department : null,
    mfaEnabled: typeof value.mfaEnabled === 'boolean' ? value.mfaEnabled : false,
    lastLoginAt: typeof value.lastLoginAt === 'string' ? new Date(value.lastLoginAt) : null,
    loginCount: typeof value.loginCount === 'number' ? value.loginCount : 0,
    isActive: typeof value.isActive === 'boolean' ? value.isActive : false,
    roles: Array.isArray(value.roles) ? value.roles.filter((role): role is string => typeof role === 'string') : [],
  };
};

// ロールの説明定義
const ROLE_DESCRIPTIONS: Record<string, { displayName: string; description: string; permissions: string; color: string }> = {
  super_admin: {
    displayName: 'スーパー管理者',
    description: 'システム全体の完全な管理権限を持ちます。全ての機能にアクセス可能です。',
    permissions: '全機能へのフルアクセス',
    color: 'red',
  },
  user_manager: {
    displayName: 'ユーザー管理者',
    description: 'ユーザーと会社の管理を担当します。',
    permissions: 'ユーザー: 作成・編集・削除、会社: 閲覧・編集',
    color: 'blue',
  },
  content_manager: {
    displayName: 'コンテンツ管理者',
    description: '業務データ（設備・メンテナンス・作業指示）の管理を担当します。',
    permissions: '設備・メンテナンス・作業指示: フルアクセス',
    color: 'green',
  },
  auditor: {
    displayName: '監査担当者',
    description: 'システム全体の監査を担当します。データの閲覧と監査ログのエクスポートが可能です。',
    permissions: '全機能: 閲覧のみ、監査ログ: 閲覧・エクスポート',
    color: 'purple',
  },
  support: {
    displayName: 'サポート担当',
    description: 'ユーザーサポートを担当します。',
    permissions: 'ユーザー: 閲覧のみ、サポートチケット: フルアクセス',
    color: 'orange',
  },
  agency_manager: {
    displayName: '代理店管理者',
    description: '代理店の管理を担当します。代理店の登録・編集・手数料管理が可能です。',
    permissions: '代理店: フルアクセス、手数料管理: フルアクセス',
    color: 'cyan',
  },
};

// ロール名から表示色を取得
const getRoleColor = (roleName: string): string => {
  return ROLE_DESCRIPTIONS[roleName]?.color || 'default';
};

// ロール名から表示名を取得
const getRoleDisplayName = (roleName: string): string => {
  return ROLE_DESCRIPTIONS[roleName]?.displayName || roleName;
};

export function AdminUserManagementPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [search, setSearch] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [roleForm] = Form.useForm();

  const { data, isLoading, refetch } = trpc.adminUserManagement.list.useQuery({
    page,
    limit,
    search: search || undefined,
  });

  const { data: rolesData } = trpc.adminUserManagement.getRoles.useQuery();

  const users = useMemo(
    () => (data?.users ?? []).flatMap((user) => {
      const normalized = normalizeAdminUser(user);
      return normalized ? [normalized] : [];
    }),
    [data?.users]
  );

  const createMutation = trpc.adminUserManagement.create.useMutation({
    onSuccess: () => {
      messageApi.success('管理ユーザーを作成しました');
      setIsCreateModalOpen(false);
      createForm.resetFields();
      refetch();
    },
    onError: (error) => {
      messageApi.error(error.message || '管理ユーザーの作成に失敗しました');
    },
  });

  const updateMutation = trpc.adminUserManagement.update.useMutation({
    onSuccess: () => {
      messageApi.success('管理ユーザーを更新しました');
      setIsEditModalOpen(false);
      editForm.resetFields();
      refetch();
    },
    onError: (error) => {
      messageApi.error(error.message || '管理ユーザーの更新に失敗しました');
    },
  });

  const deleteMutation = trpc.adminUserManagement.delete.useMutation({
    onSuccess: () => {
      messageApi.success('管理ユーザーを削除しました');
      refetch();
    },
    onError: (error) => {
      messageApi.error(error.message || '管理ユーザーの削除に失敗しました');
    },
  });

  const assignRolesMutation = trpc.adminUserManagement.assignRoles.useMutation({
    onSuccess: () => {
      messageApi.success('ロールを更新しました');
      setIsRoleModalOpen(false);
      roleForm.resetFields();
      refetch();
    },
    onError: (error) => {
      messageApi.error(error.message || 'ロールの更新に失敗しました');
    },
  });

  const handleCreate = async (values: Record<string, unknown>) => {
    await createMutation.mutateAsync(values);
  };

  const handleEdit = (user: AdminUser) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      username: user.username,
      email: user.email,
      displayName: user.displayName,
      department: user.department,
      isActive: user.isActive,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (values: Record<string, unknown>) => {
    if (!selectedUser) return;
    await updateMutation.mutateAsync({
      id: selectedUser.id,
      ...values,
    });
  };

  const handleDelete = async (userId: string) => {
    await deleteMutation.mutateAsync({ id: userId });
  };

  const handleAssignRoles = (user: AdminUser) => {
    setSelectedUser(user);
    const userRoleIds =
      rolesData?.filter((r) => user.roles.includes(r.name)).map((r) => r.id) || [];
    roleForm.setFieldsValue({ roleIds: userRoleIds });
    setIsRoleModalOpen(true);
  };

  const handleRoleSubmit = async (values: { roleIds: string[] }) => {
    if (!selectedUser) return;
    await assignRolesMutation.mutateAsync({
      userId: selectedUser.id,
      roleIds: values.roleIds,
    });
  };

  const columns: ColumnsType<AdminUser> = [
    {
      title: 'ユーザー名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '表示名',
      dataIndex: 'displayName',
      key: 'displayName',
      width: 150,
    },
    {
      title: (
        <Space>
          ロール
          <Tooltip title="ユーザーに割り当てられた権限ロールです">
            <QuestionCircleOutlined style={{ color: '#999' }} />
          </Tooltip>
        </Space>
      ),
      dataIndex: 'roles',
      key: 'roles',
      width: 220,
      render: (roles: string[]) =>
        roles.length > 0 ? (
          <Space wrap>
            {roles.map((role) => (
              <Tooltip key={role} title={ROLE_DESCRIPTIONS[role]?.description || ''}>
                <Tag color={getRoleColor(role)}>
                  {getRoleDisplayName(role)}
                </Tag>
              </Tooltip>
            ))}
          </Space>
        ) : (
          <Tag color="default">ロールなし</Tag>
        ),
    },
    {
      title: 'MFA',
      dataIndex: 'mfaEnabled',
      key: 'mfaEnabled',
      width: 80,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'default'}>{enabled ? '有効' : '無効'}</Tag>
      ),
    },
    {
      title: '最終ログイン',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 160,
      render: (date: Date | null) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: 'ステータス',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? '有効' : '無効'}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          <Button
            type="link"
            size="small"
            icon={<KeyOutlined />}
            onClick={() => handleAssignRoles(record)}
          >
            ロール
          </Button>
          <Popconfirm
            title="この管理ユーザーを削除しますか？"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              削除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      {contextHolder}

      <PageHeader
        title="管理ユーザー管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateModalOpen(true)}>
            新規作成
          </Button>
        }
      />

      {/* ロール説明セクション */}
      <Collapse
        ghost
        style={{ marginBottom: 16, background: '#fafafa', borderRadius: 8 }}
        items={[
          {
            key: 'role-info',
            label: (
              <Space>
                <InfoCircleOutlined />
                <Text strong>ロール（権限）について</Text>
              </Space>
            ),
            children: (
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  管理ユーザーには以下のロールを割り当てることができます。複数のロールを同時に割り当てることも可能です。
                </Text>
                <Descriptions
                  column={1}
                  size="small"
                  bordered
                  labelStyle={{ width: 180, fontWeight: 500 }}
                >
                  {Object.entries(ROLE_DESCRIPTIONS).map(([key, value]) => (
                    <Descriptions.Item
                      key={key}
                      label={
                        <Tag color={value.color} style={{ margin: 0 }}>
                          {value.displayName}
                        </Tag>
                      }
                    >
                      <div>
                        <div>{value.description}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          権限: {value.permissions}
                        </Text>
                      </div>
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              </div>
            ),
          },
        ]}
      />

      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>

          <Space wrap>
            <Input.Search
              placeholder="ユーザー名、メールアドレス、表示名で検索"
              style={{ width: 300 }}
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
              allowClear
            />
          </Space>

          <Table
            columns={columns}
            dataSource={users}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: limit,
              total: data?.total || 0,
              showSizeChanger: false,
              showTotal: (total) => `全 ${total} 件`,
              onChange: (newPage) => setPage(newPage),
            }}
            scroll={{ x: 1400 }}
          />
        </Space>
      </Card>

      {/* 新規作成モーダル */}
      <Modal
        title="管理ユーザー新規作成"
        open={isCreateModalOpen}
        onCancel={() => {
          setIsCreateModalOpen(false);
          createForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={createForm} onFinish={handleCreate} layout="vertical">
          <Form.Item
            name="username"
            label="ユーザー名"
            rules={[{ required: true, message: 'ユーザー名を入力してください' }]}
          >
            <Input placeholder="user123" />
          </Form.Item>

          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[
              { required: true, message: 'メールアドレスを入力してください' },
              { type: 'email', message: '有効なメールアドレスを入力してください' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label="パスワード"
            rules={[
              { required: true, message: 'パスワードを入力してください' },
              { min: 8, message: 'パスワードは8文字以上で入力してください' },
            ]}
          >
            <Input.Password placeholder="8文字以上のパスワード" />
          </Form.Item>

          <Form.Item
            name="displayName"
            label="表示名"
            rules={[{ required: true, message: '表示名を入力してください' }]}
          >
            <Input placeholder="山田 太郎" />
          </Form.Item>

          <Form.Item name="department" label="部署">
            <Input placeholder="システム管理部" />
          </Form.Item>

          <Form.Item name="employeeId" label="社員番号">
            <Input placeholder="E12345" />
          </Form.Item>

          <Form.Item
            name="roleIds"
            label={
              <Space>
                ロール
                <Tooltip title="管理ユーザーに付与する権限を選択します">
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            }
          >
            <Select mode="multiple" placeholder="ロールを選択">
              {rolesData?.map((role) => (
                <Select.Option key={role.id} value={role.id}>
                  <Space>
                    <Tag color={getRoleColor(role.name)} style={{ margin: 0 }}>
                      {role.displayName}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {ROLE_DESCRIPTIONS[role.name]?.permissions || ''}
                    </Text>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
                作成
              </Button>
              <Button onClick={() => setIsCreateModalOpen(false)}>キャンセル</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 編集モーダル */}
      <Modal
        title="管理ユーザー編集"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={editForm} onFinish={handleUpdate} layout="vertical">
          <Form.Item name="username" label="ユーザー名">
            <Input disabled />
          </Form.Item>

          <Form.Item
            name="email"
            label="メールアドレス"
            rules={[{ type: 'email', message: '有効なメールアドレスを入力してください' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="displayName" label="表示名">
            <Input />
          </Form.Item>

          <Form.Item name="department" label="部署">
            <Input />
          </Form.Item>

          <Form.Item name="isActive" label="ステータス">
            <Select>
              <Select.Option value={true}>有効</Select.Option>
              <Select.Option value={false}>無効</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
                更新
              </Button>
              <Button onClick={() => setIsEditModalOpen(false)}>キャンセル</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ロール割り当てモーダル */}
      <Modal
        title={`ロール割り当て - ${selectedUser?.displayName}`}
        open={isRoleModalOpen}
        onCancel={() => {
          setIsRoleModalOpen(false);
          roleForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={roleForm} onFinish={handleRoleSubmit} layout="vertical">
          {/* ロール説明 */}
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              ロールはユーザーがアクセスできる機能を決定します。複数のロールを同時に割り当てることができます。
            </Text>
          </div>

          <Form.Item
            name="roleIds"
            label="ロール"
          >
            <Select mode="multiple" placeholder="ロールを選択" optionLabelProp="label">
              {rolesData?.map((role) => (
                <Select.Option
                  key={role.id}
                  value={role.id}
                  label={role.displayName}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Space>
                      <Tag color={getRoleColor(role.name)} style={{ margin: 0 }}>
                        {role.displayName}
                      </Tag>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {ROLE_DESCRIPTIONS[role.name]?.description || ''}
                    </Text>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* 選択中のロールの詳細 */}
          {selectedUser && selectedUser.roles.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>現在のロール:</Text>
              <div style={{ marginTop: 8 }}>
                {selectedUser.roles.map((role) => (
                  <div key={role} style={{ marginBottom: 4 }}>
                    <Tag color={getRoleColor(role)}>{getRoleDisplayName(role)}</Tag>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                      {ROLE_DESCRIPTIONS[role]?.permissions || ''}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={assignRolesMutation.isPending}>
                保存
              </Button>
              <Button onClick={() => setIsRoleModalOpen(false)}>キャンセル</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
}
